import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [parents, setParents] = useState([]);
  const [childrenRecords, setChildrenRecords] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [reports, setReports] = useState(null);
  const [fleetLocations, setFleetLocations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [handoverLogs, setHandoverLogs] = useState([]);

  const request = useCallback(async (method, url, { data, params, trackLoading = false, responseType } = {}) => {
    if (trackLoading) setLoading(true);
    try {
      const response = await axios({ method, url: `${API_BASE}${url}`, data, params, responseType });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Something went wrong",
      };
    } finally {
      if (trackLoading) setLoading(false);
    }
  }, []);

  const downloadCsv = useCallback(async (path, fallbackFileName) => {
    const result = await request("get", path, { responseType: "blob" });
    if (!result.success) return result;
    const blobUrl = window.URL.createObjectURL(result.data);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fallbackFileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(blobUrl);
    return { success: true };
  }, [request]);

  const loadSettings = useCallback(async () => {
    const result = await request("get", "/settings");
    if (result.success) setCompanySettings(result.data);
    return result;
  }, [request]);

  const saveSettings = useCallback(async (payload) => {
    const result = await request("put", "/settings", { data: payload, trackLoading: true });
    if (result.success) setCompanySettings(result.data);
    return result;
  }, [request]);

  const loadOverview = useCallback(async () => {
    const result = await request("get", "/dashboard/overview", { trackLoading: true });
    if (result.success) setOverview(result.data);
    return result;
  }, [request]);

  const loadTrips = useCallback(async (filters = {}) => {
    const result = await request("get", "/trips", { params: filters, trackLoading: true });
    if (result.success) setTrips(result.data);
    return result;
  }, [request]);

  const createTrip = useCallback(async (payload) => {
    const result = await request("post", "/trips", { data: payload, trackLoading: true });
    if (result.success) setTrips((prev) => [result.data, ...prev]);
    return result;
  }, [request]);

  const updateTrip = useCallback(async (tripId, payload) => {
    const result = await request("put", `/trips/${tripId}`, { data: payload, trackLoading: true });
    if (result.success) setTrips((prev) => prev.map((trip) => (trip.id === tripId ? result.data : trip)));
    return result;
  }, [request]);

  const startTrip = useCallback(async (tripId, payload = { prejourney_completed: true }) => {
    const result = await request("post", `/trips/${tripId}/start`, { data: payload, trackLoading: true });
    if (result.success) setTrips((prev) => prev.map((trip) => (trip.id === tripId ? result.data : trip)));
    return result;
  }, [request]);

  const completeTrip = useCallback(async (tripId, payload) => {
    const result = await request("post", `/trips/${tripId}/complete`, { data: payload, trackLoading: true });
    if (result.success) setTrips((prev) => prev.map((trip) => (trip.id === tripId ? result.data : trip)));
    return result;
  }, [request]);

  const confirmArrival = useCallback(async (tripId) => {
    const result = await request("post", `/trips/${tripId}/confirm-arrival`, { trackLoading: true });
    if (result.success) setTrips((prev) => prev.map((trip) => (trip.id === tripId ? result.data : trip)));
    return result;
  }, [request]);

  const loadVehicles = useCallback(async () => {
    const result = await request("get", "/vehicles", { trackLoading: true });
    if (result.success) setVehicles(result.data);
    return result;
  }, [request]);

  const createVehicle = useCallback(async (payload) => {
    const result = await request("post", "/vehicles", { data: payload, trackLoading: true });
    if (result.success) setVehicles((prev) => [result.data, ...prev]);
    return result;
  }, [request]);

  const updateVehicle = useCallback(async (vehicleId, payload) => {
    const result = await request("put", `/vehicles/${vehicleId}`, { data: payload, trackLoading: true });
    if (result.success) setVehicles((prev) => prev.map((vehicle) => (vehicle.id === vehicleId ? result.data : vehicle)));
    return result;
  }, [request]);

  const loadIncidents = useCallback(async () => {
    const result = await request("get", "/incidents", { trackLoading: true });
    if (result.success) setIncidents(result.data);
    return result;
  }, [request]);

  const createIncident = useCallback(async (payload) => {
    const result = await request("post", "/incidents", { data: payload, trackLoading: true });
    if (result.success) setIncidents((prev) => [result.data, ...prev]);
    return result;
  }, [request]);

  const updateIncident = useCallback(async (incidentId, payload) => {
    const result = await request("put", `/incidents/${incidentId}`, { data: payload, trackLoading: true });
    if (result.success) setIncidents((prev) => prev.map((incident) => (incident.id === incidentId ? result.data : incident)));
    return result;
  }, [request]);

  const loadUsers = useCallback(async (role = null, statusFilter = null) => {
    const params = {};
    if (role) params.role = role;
    if (statusFilter) params.status = statusFilter;
    const result = await request("get", "/users", { params, trackLoading: true });
    if (result.success) setUsers(result.data);
    return result;
  }, [request]);

  const createUser = useCallback(async (payload) => {
    const result = await request("post", "/users", { data: payload, trackLoading: true });
    if (result.success) setUsers((prev) => [result.data, ...prev]);
    return result;
  }, [request]);

  const updateUser = useCallback(async (userId, payload) => {
    const result = await request("put", `/users/${userId}`, { data: payload, trackLoading: true });
    if (result.success) setUsers((prev) => prev.map((record) => (record.id === userId ? result.data : record)));
    return result;
  }, [request]);

  const generateResetLink = useCallback(async (userId) => request("post", `/users/${userId}/password-reset`, { trackLoading: true }), [request]);

  const loadSchools = useCallback(async () => {
    const result = await request("get", "/schools", { trackLoading: true });
    if (result.success) setSchools(result.data);
    return result;
  }, [request]);

  const createSchool = useCallback(async (payload) => {
    const result = await request("post", "/schools", { data: payload, trackLoading: true });
    if (result.success) setSchools((prev) => [result.data, ...prev]);
    return result;
  }, [request]);

  const updateSchool = useCallback(async (schoolId, payload) => {
    const result = await request("put", `/schools/${schoolId}`, { data: payload, trackLoading: true });
    if (result.success) setSchools((prev) => prev.map((record) => (record.id === schoolId ? result.data : record)));
    return result;
  }, [request]);

  const loadParents = useCallback(async () => {
    const result = await request("get", "/parents", { trackLoading: true });
    if (result.success) setParents(result.data);
    return result;
  }, [request]);

  const updateParent = useCallback(async (parentId, payload) => {
    const result = await request("put", `/parents/${parentId}`, { data: payload, trackLoading: true });
    if (result.success) setParents((prev) => prev.map((record) => (record.id === parentId ? result.data : record)));
    return result;
  }, [request]);

  const loadChildren = useCallback(async () => {
    const result = await request("get", "/children", { trackLoading: true });
    if (result.success) setChildrenRecords(result.data);
    return result;
  }, [request]);

  const createChild = useCallback(async (payload) => {
    const result = await request("post", "/children", { data: payload, trackLoading: true });
    if (result.success) setChildrenRecords((prev) => [result.data, ...prev]);
    return result;
  }, [request]);

  const updateChild = useCallback(async (childId, payload) => {
    const result = await request("put", `/children/${childId}`, { data: payload, trackLoading: true });
    if (result.success) setChildrenRecords((prev) => prev.map((record) => (record.id === childId ? result.data : record)));
    return result;
  }, [request]);

  const loadReports = useCallback(async () => {
    const result = await request("get", "/reports/summary", { trackLoading: true });
    if (result.success) setReports(result.data);
    return result;
  }, [request]);

  const downloadReport = useCallback(async (type) => {
    const routes = {
      trips: "/reports/trips.csv",
      incidents: "/reports/incidents.csv",
      handovers: "/reports/handover.csv",
    };
    const filenames = {
      trips: "carefleet-trip-report.csv",
      incidents: "carefleet-incident-report.csv",
      handovers: "carefleet-handover-report.csv",
    };
    return downloadCsv(routes[type], filenames[type]);
  }, [downloadCsv]);

  const loadFleetLocations = useCallback(async () => {
    const result = await request("get", "/fleet/locations");
    if (result.success) setFleetLocations(result.data);
    return result;
  }, [request]);

  const sendLocationUpdate = useCallback(async (payload) => request("post", "/location/update", { data: payload }), [request]);
  const getLiveLocation = useCallback(async (tripId) => request("get", `/location/live/${tripId}`), [request]);
  const getTrackingByToken = useCallback(async (token) => request("get", `/tracking/${token}`), [request]);

  const loadNotifications = useCallback(async () => {
    const result = await request("get", "/notifications", { trackLoading: true });
    if (result.success) setNotifications(result.data);
    return result;
  }, [request]);

  const loadHandoverLogs = useCallback(async (tripId = null) => {
    const result = await request("get", "/handover/logs", { params: tripId ? { trip_id: tripId } : {}, trackLoading: true });
    if (result.success) setHandoverLogs(result.data);
    return result;
  }, [request]);

  const generateHandoverToken = useCallback(async (tripId, flow) => request("post", "/handover/tokens", { data: { trip_id: tripId, flow }, trackLoading: true }), [request]);

  const verifyHandoverToken = useCallback(async (token, method = "qr") => {
    const result = await request("post", "/handover/verify", { data: { token, method }, trackLoading: true });
    if (result.success && result.data?.trip) {
      setTrips((prev) => prev.map((trip) => (trip.id === result.data.trip.id ? result.data.trip : trip)));
    }
    return result;
  }, [request]);

  const value = useMemo(() => ({
    loading,
    overview,
    trips,
    vehicles,
    incidents,
    users,
    schools,
    parents,
    childrenRecords,
    companySettings,
    reports,
    fleetLocations,
    notifications,
    handoverLogs,
    loadSettings,
    saveSettings,
    loadOverview,
    loadTrips,
    createTrip,
    updateTrip,
    startTrip,
    completeTrip,
    confirmArrival,
    loadVehicles,
    createVehicle,
    updateVehicle,
    loadIncidents,
    createIncident,
    updateIncident,
    loadUsers,
    createUser,
    updateUser,
    generateResetLink,
    loadSchools,
    createSchool,
    updateSchool,
    loadParents,
    updateParent,
    loadChildren,
    createChild,
    updateChild,
    loadReports,
    downloadReport,
    loadFleetLocations,
    sendLocationUpdate,
    getLiveLocation,
    getTrackingByToken,
    loadNotifications,
    loadHandoverLogs,
    generateHandoverToken,
    verifyHandoverToken,
  }), [
    loading,
    overview,
    trips,
    vehicles,
    incidents,
    users,
    schools,
    parents,
    childrenRecords,
    companySettings,
    reports,
    fleetLocations,
    notifications,
    handoverLogs,
    loadSettings,
    saveSettings,
    loadOverview,
    loadTrips,
    createTrip,
    updateTrip,
    startTrip,
    completeTrip,
    confirmArrival,
    loadVehicles,
    createVehicle,
    updateVehicle,
    loadIncidents,
    createIncident,
    updateIncident,
    loadUsers,
    createUser,
    updateUser,
    generateResetLink,
    loadSchools,
    createSchool,
    updateSchool,
    loadParents,
    updateParent,
    loadChildren,
    createChild,
    updateChild,
    loadReports,
    downloadReport,
    loadFleetLocations,
    sendLocationUpdate,
    getLiveLocation,
    getTrackingByToken,
    loadNotifications,
    loadHandoverLogs,
    generateHandoverToken,
    verifyHandoverToken,
  ]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;
